import { Feather } from '@expo/vector-icons';
import { useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import { CameraView, useCameraPermissions } from 'expo-camera';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View, StyleSheet, TouchableOpacity, Linking } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { showMessage } from 'react-native-flash-message';

import { Text } from '_/space-management/components';
import {
  QRCODE_LOCATION_PREFIX,
  QRCODE_SLOT_PREFIX,
  QRCODE_SPACE_PREFIX,
} from '_/space-management/config/qrCodePrefix';
import { colors } from '_/space-management/constants/theme';
import { formatDate } from '_/space-management/helpers/formatDate';
import { networkErrorMessage } from '_/space-management/helpers/networkError';
import { useEventContext } from '_/space-management/hooks/EventContext';
import { eventsApi } from '_/space-management/services/api';
import { EventsResponse } from '_/space-management/services/models/events.model';

import styles from './styles';

interface ParamsProps {
  event: EventsResponse;
}

export default function QRCodeScanner() {
  const route = useRoute();
  const params = route.params as ParamsProps;
  const { event } = params;

  const { getTodayEvents } = useEventContext();
  const { t } = useTranslation();
  const navigation = useNavigation<any>();

  const isFocused = useIsFocused();
  const [scanned, setScanned] = useState(false);
  const [loading, setLoading] = useState(false);
  const [permission, requestPermission] = useCameraPermissions();

  useEffect(() => {
    requestPermission();
  }, []);

  const handleQRCodeScanned = async (data: string) => {
    setLoading(true);
    try {
      const prefix = data.slice(0, data.lastIndexOf('/') + 1);
      // console.log('- Qr Code: ', prefix, QRCODE_SLOT_PREFIX, QRCODE_LOCATION_PREFIX, QRCODE_SPACE_PREFIX);
      // TODO: perguntar ao Roberto sobre esses "cases"
      switch (prefix) {
        // case QRCODE_SLOT_PREFIX:
        case prefix:
          // const slotId = data.replace(QRCODE_SLOT_PREFIX, '');
          const slotId = data.replace(prefix, '');
          if (slotId !== event.slotId) {
            throwError('qrCodeScreen.errorMessages.wrongSlotQrCode');
            return;
          }
          await eventsApi.checkinByQrCode({ id: event.id, slotId });
          break;

        case QRCODE_LOCATION_PREFIX:
          const locationId = data.replace(QRCODE_LOCATION_PREFIX, '');
          if (locationId !== event.slot?.space?.location?.id) {
            throwError('qrCodeScreen.errorMessages.wrongSlotQrCode');
            return;
          }
          await eventsApi.checkinByQrCode({ id: event.id, slotId: event.slotId });
          break;

        case QRCODE_SPACE_PREFIX:
          const spaceId = data.replace(QRCODE_SPACE_PREFIX, '');
          if (spaceId !== event.slot?.space?.id) {
            throwError('qrCodeScreen.errorMessages.wrongSlotQrCode');
            return;
          }
          await eventsApi.checkinByQrCode({ id: event.id, slotId: event.slotId });
          break;

        default:
          throwError('qrCodeScreen.errorMessages.invalidQrCode');
          return;
      }

      showMessage({
        message: t('spaceManage.success'),
        description: t('spaceManage.qrCodeScreen.successMessages.checkinSuccess'),
        backgroundColor: colors.successGreen,
      });

      navigation.goBack();
      await getTodayEvents();
    } catch (error: any) {
      networkErrorMessage(error);
      const errorData = error?.response?.data?.data;
      handleCheckinErrors(errorData);
    } finally {
      setLoading(false);
    }
  };

  const throwError = (msgKey: string) => {
    showMessage({
      message: t('spaceManage.error'),
      description: t(msgKey),
      backgroundColor: colors.errorRed,
    });
  };

  const handleCheckinErrors = (errorData: any) => {
    switch (errorData?.type) {
      case 'slotNotAvailable':
        throwError('qrCodeScreen.errorMessages.slotNotAvailable');
        break;
      case 'SlotAlreadyReserved':
        showMessage({
          message: t('spaceManage.error'),
          description: t('spaceManage.qrCodeScreen.errorMessages.isThereAnotherReservation', {
            endAt: formatDate(new Date(errorData?.currentEventEndAt), 'HH:mm'),
          }),
          backgroundColor: colors.errorRed,
        });
        break;
      case 'checkinAfterEnded':
        throwError('qrCodeScreen.errorMessages.checkinAfterEnded');
        break;
      case 'checkinAfterTolerance':
        showMessage({
          message: t('spaceManage.error'),
          description: t('spaceManage.qrCodeScreen.errorMessages.checkinAfterTolerance', {
            spaceTolerance: errorData?.spaceTolerance,
          }),
          backgroundColor: colors.errorRed,
        });
        break;
      default:
        throwError('qrCodeScreen.errorMessages.checkinFail');
        break;
    }
  };

  const onBarCodeScanned = ({ data }: { data: string }) => {
    console.log('- QrCode Data: ', data);
    if (!scanned && !loading) {
      setScanned(true);
      handleQRCodeScanned(data);
      setTimeout(() => setScanned(false), 1000);
    }
  };

  const openSettings = async () => {
    await Linking.openSettings();
    navigation.goBack();
  };

  const renderPermissionDenied = () => (
    <View style={styles.titleContainer}>
      <Feather name="camera-off" size={24} color={colors.attentionYellow} />
      <Text fontType="bold" center fontSize={28} color={colors.white} style={styles.title}>
        {t('spaceManage.qrCodeScreen.noAccessToCamera')}
      </Text>
      <Text center fontSize={16} color={colors.white}>
        {t('spaceManage.qrCodeScreen.openConfigToAllow')}
      </Text>
      <TouchableOpacity onPress={openSettings} style={styles.button}>
        <Text center fontSize={18} fontType="bold" color={colors.clear}>
          {t('spaceManage.qrCodeScreen.openSettings')}
        </Text>
      </TouchableOpacity>
    </View>
  );

  const renderCamera = () => (
    <CameraView
      onBarcodeScanned={scanned || loading ? undefined : onBarCodeScanned}
      barcodeScannerSettings={{ barcodeTypes: ['qr'] }}
      style={StyleSheet.absoluteFillObject}
    />
  );

  return (
    <View style={styles.container}>
      <View style={styles.cameraContainer}>
        {isFocused && permission?.granted && renderCamera()}

        <View style={styles.layerTop}>
          <View style={styles.titleContainer}>
            <Text center fontType="bold" fontSize={28} color={colors.white} style={styles.title}>
              {t('spaceManage.qrCodeScreen.qrCode')}
            </Text>
            <Text center fontSize={16} color={colors.white}>
              {t('spaceManage.qrCodeScreen.qrCodeMessage')}
            </Text>
          </View>
        </View>
        <View style={styles.layerCenter}>
          <View style={styles.layerLeft} />
          <Svg width="280" height="280" viewBox="0 0 280 280" fill="none">
            <Path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M12 0H0V12C0 5.37262 5.37256 0 12 0ZM268 0C274.627 0 280 5.37256 280 12V0H268ZM280 268C280 274.627 274.627 280 268 280H280V268ZM12 280C5.37256 280 0 274.627 0 268V280H12Z"
              fill={colors.black}
              fillOpacity="0.75"
            />
            <Path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M26 20C22.6863 20 20 22.6863 20 26V50L22.8977 28.0604C23.253 25.37 25.37 23.253 28.0604 22.8977L50 20H26ZM20 254C20 257.314 22.6863 260 26 260H50L28.0604 257.102C25.37 256.747 23.253 254.63 22.8977 251.94L20 230V254ZM254 20C257.314 20 260 22.6863 260 26V50L257.102 28.0604C256.747 25.37 254.63 23.253 251.94 22.8977L230 20H254ZM254 260C257.314 260 260 257.314 260 254V230L257.102 251.94C256.747 254.63 254.63 256.747 251.94 257.102L230 260H254Z"
              fill="white"
              fillOpacity="0.5"
            />
          </Svg>
          <View style={styles.layerRight} />
        </View>
        <View style={styles.layerBottom} />
      </View>

      {!permission?.granted && renderPermissionDenied()}

      <TouchableOpacity onPress={navigation.goBack} style={styles.button}>
        <Text center fontSize={18} fontType="bold" color={colors.clear}>
          {t('spaceManage.qrCodeScreen.closeReader')}
        </Text>
      </TouchableOpacity>
    </View>
  );
}
